import { pickDefaultPreviewLanguage } from './preview.utils';

// Every session of a program outline (modules → units → sessions), in teaching order.
export const listProgramSessions = (modules = []) =>
    (modules || []).flatMap(mod => (mod?.units || []).flatMap(unit => (unit?.sessions || [])
        .filter(session => session?.session_code)
        .map(session => ({
            session_code: session.session_code,
            session_title: session.session_title || session.session_code,
            module_title: mod.module_title || '',
            unit_title: unit.unit_title || '',
        }))));

// One session's slides from an unrestricted slide_preview response, flattened in segment order
// (Intro, SLO1…, Closing). Uses the same default language as the preview page.
export const flattenSessionSlides = (resData) => {
    const byLang = resData?.slide_previews_by_language;
    const langs = byLang && typeof byLang === 'object' ? Object.keys(byLang) : [];
    let language = 'en-US';
    let langData = null;
    if (langs.length) {
        language = pickDefaultPreviewLanguage(langs);
        if (!byLang[language]) language = langs[0];
        langData = byLang[language];
    } else if (resData?.slide_preview) {
        langData = resData;
    }
    const deck = langData?.slide_preview || {};
    const slides = Object.keys(deck)
        .filter(segment => Array.isArray(deck[segment]) && segment !== 'Seg5_1')
        .flatMap(segment => deck[segment].filter(Boolean));
    return { language, slides, videoUrl: langData?.IntroVideo || null };
};

const toPx = (value, unit) => (unit === 'em' || unit === 'rem' ? value * 16 : value);

// Whether a width-only media query list matches a viewport `width` px wide: true/false, or null when the
// query has anything other than (min-width)/(max-width) features (or no width at all) and is left alone.
export const widthQueryMatches = (mediaText, width) => {
    if (!/width/.test(mediaText || '')) return null;
    let known = true;
    const matches = mediaText.split(',').some(query => (query.match(/\([^)]*\)/g) || []).every((feature) => {
        const m = feature.match(/^\(\s*(min|max)-width\s*:\s*([\d.]+)(px|em|rem)\s*\)$/);
        if (!m) {
            known = false;
            return false;
        }
        const px = toPx(parseFloat(m[2]), m[3]);
        return m[1] === 'min' ? width >= px : width <= px;
    }));
    return known ? matches : null;
};

// Width the exported deck is laid out at (the preview's desktop layout).
export const DECK_WIDTH = 1280;

// Rewrites relative url(...) references so the CSS still resolves once saved to a standalone file.
export const absolutizeCssUrls = (cssText, base) => cssText.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/g, (match, quote, url) => {
    if (/^(data:|https?:|blob:|#)/i.test(url)) return match;
    try {
        return `url("${new URL(url, base).href}")`;
    } catch (e) {
        return match;
    }
});

const IMPORT_RULE = 3;
const MEDIA_RULE = 4;
const PAGE_RULE = 6;

const serializeRules = (rules, width, imports, out) => {
    Array.from(rules).forEach((rule) => {
        if (rule.type === PAGE_RULE) return;
        if (rule.type === IMPORT_RULE) {
            imports.push(rule.cssText);
            return;
        }
        if (rule.type === MEDIA_RULE) {
            const hit = widthQueryMatches(rule.media.mediaText, width);
            if (hit === false) return;
            if (hit === true) {
                serializeRules(rule.cssRules, width, imports, out);
                return;
            }
        }
        out.push(rule.cssText);
    });
};

// All CSS the document currently applies, as text, with width media queries resolved as on a `width`-px screen
// so the saved file shows the same layout in any window. Cross-origin sheets (web fonts) are kept as @import.
export const collectDeckCss = (doc, width = DECK_WIDTH) => {
    const imports = [];
    const out = [];
    Array.from(doc.styleSheets).forEach((sheet) => {
        let rules;
        try {
            rules = sheet.cssRules;
        } catch (e) {
            if (sheet.href) imports.push(`@import url("${sheet.href}");`);
            return;
        }
        const sheetRules = [];
        serializeRules(rules, width, imports, sheetRules);
        const base = sheet.href || doc.baseURI;
        sheetRules.forEach(css => out.push(absolutizeCssUrls(css, base)));
    });
    return [...imports, ...out].join('\n');
};

const escapeHtml = text => String(text).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// A standalone HTML document for the exported deck.
export const buildDeckHtml = ({ title, css, bodyHtml }) => `<!doctype html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=${DECK_WIDTH + 48}">
<title>${escapeHtml(title)}</title>
<style>${css.replace(/<\/style/gi, '<\\/style')}</style>
</head><body style="margin:0;background:#E5E7EB">${bodyHtml}</body></html>
`;
